import { useMemo, useState } from "react";
import {
  ArrowRight,
  Calculator,
  ChevronRight,
  CircleEqual,
  Coins,
  RotateCcw,
  TrendingDown,
} from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { RevealSection } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSite } from "@/hooks/use-site";
import type { Messages } from "@/lib/i18n";
import { localeByCode } from "@/lib/locale-registry";
import { localizeUrl } from "@/lib/site";
import {
  calculateTco,
  DEFAULT_TCO_ASSUMPTIONS,
  DEFAULT_TCO_SCENARIOS,
  type TcoBreakdown,
  type TcoScenario,
} from "@/lib/tco";

const CURRENCIES = ["EUR", "USD", "GBP", "PLN", "UAH", "BRL"] as const;
type Currency = (typeof CURRENCIES)[number];

type ScenarioField = {
  key: keyof TcoScenario;
  labelKey: keyof Messages;
  step: number;
};

const SCENARIO_FIELDS: ReadonlyArray<ScenarioField> = [
  { key: "unitPrice", labelKey: "tcoFieldUnitPrice", step: 1 },
  { key: "logisticsPerDevice", labelKey: "tcoFieldLogistics", step: 0.5 },
  { key: "oneTimeSetup", labelKey: "tcoFieldSetup", step: 100 },
  { key: "annualFailureRatePct", labelKey: "tcoFieldFailureRate", step: 0.1 },
  { key: "replacementIncidentCost", labelKey: "tcoFieldReplacement", step: 1 },
  { key: "annualSupportContactsPer1000", labelKey: "tcoFieldSupportContacts", step: 10 },
  { key: "supportContactCost", labelKey: "tcoFieldSupportCost", step: 0.5 },
  { key: "annualTruckRollsPer1000", labelKey: "tcoFieldTruckRolls", step: 1 },
  { key: "truckRollCost", labelKey: "tcoFieldTruckRollCost", step: 1 },
  { key: "dmsPerDevicePerMonth", labelKey: "tcoFieldDmsCost", step: 0.01 },
];

const BREAKDOWN_FIELDS: ReadonlyArray<{
  key: keyof TcoBreakdown;
  labelKey: keyof Messages;
}> = [
  { key: "procurement", labelKey: "tcoCostProcurement" },
  { key: "logistics", labelKey: "tcoCostLogistics" },
  { key: "setup", labelKey: "tcoCostSetup" },
  { key: "replacements", labelKey: "tcoCostReplacements" },
  { key: "support", labelKey: "tcoCostSupport" },
  { key: "fieldService", labelKey: "tcoCostFieldService" },
  { key: "management", labelKey: "tcoCostManagement" },
];

function cloneDefaultScenarios(): [TcoScenario, TcoScenario] {
  return [{ ...DEFAULT_TCO_SCENARIOS[0] }, { ...DEFAULT_TCO_SCENARIOS[1] }];
}

function inputValue(value: number): number | "" {
  return Number.isFinite(value) ? value : "";
}

function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)}/g, (_, key: string) => values[key] ?? "");
}

export function StbTcoCalculatorPage() {
  const { messages, site, href } = useSite();
  const u = (path: string) => localizeUrl(path, site.locale, href);
  const [currency, setCurrency] = useState<Currency>("EUR");
  const [assumptions, setAssumptions] = useState({ ...DEFAULT_TCO_ASSUMPTIONS });
  const [scenarios, setScenarios] = useState<[TcoScenario, TcoScenario]>(cloneDefaultScenarios);

  const results = useMemo(
    () => scenarios.map((scenario) => calculateTco(assumptions, scenario)),
    [assumptions, scenarios],
  );
  const locale = localeByCode(site.locale).intl;
  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }),
    [currency, locale],
  );
  const preciseMoney = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [currency, locale],
  );

  const scenarioLabels = [messages.tcoScenarioA, messages.tcoScenarioB] as const;
  const difference = Math.abs(results[0].total - results[1].total);
  const lowerIndex = results[0].total <= results[1].total ? 0 : 1;
  const higherTotal = Math.max(results[0].total, results[1].total);
  const differencePercent = higherTotal > 0 ? (difference / higherTotal) * 100 : 0;
  const isEqual = difference < 0.01;

  const updateScenario = (index: 0 | 1, key: keyof TcoScenario, value: number) => {
    setScenarios((current) => {
      const next: [TcoScenario, TcoScenario] = [{ ...current[0] }, { ...current[1] }];
      next[index][key] = value;
      return next;
    });
  };

  const reset = () => {
    setCurrency("EUR");
    setAssumptions({ ...DEFAULT_TCO_ASSUMPTIONS });
    setScenarios(cloneDefaultScenarios());
  };

  return (
    <SiteLayout>
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={u("/")} className="hover:text-foreground">
            {messages.guidesBreadcrumbHome}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <a href={u("/guides")} className="hover:text-foreground">
            {messages.navGuides}
          </a>
          <ChevronRight aria-hidden className="h-3 w-3" />
          <span className="text-foreground">{messages.tcoBreadcrumbPage}</span>
        </nav>
      </div>

      <section className="relative overflow-hidden border-b border-border/60 bg-muted/10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-20"
        />
        <div className="mx-auto max-w-4xl px-6 py-16 text-center lg:py-24">
          <div className="mx-auto flex h-12 w-12 animate-fade-up items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Calculator aria-hidden className="h-6 w-6" />
          </div>
          <p
            className="mt-5 animate-fade-up text-sm font-medium text-primary"
            style={{ animationDelay: "80ms" }}
          >
            {messages.tcoHeroEyebrow}
          </p>
          <h1
            className="mt-3 animate-fade-up text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl"
            style={{ animationDelay: "160ms" }}
          >
            {messages.tcoHeroTitle}
          </h1>
          <p
            className="mx-auto mt-5 max-w-3xl animate-fade-up text-lg leading-relaxed text-muted-foreground"
            style={{ animationDelay: "240ms" }}
          >
            {messages.tcoHeroBody}
          </p>
          <p
            className="mx-auto mt-5 max-w-2xl animate-fade-up rounded-xl border border-border bg-background/80 px-4 py-3 text-sm text-muted-foreground"
            style={{ animationDelay: "320ms" }}
          >
            {messages.tcoExampleNotice}
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
        <RevealSection aria-labelledby="tco-assumptions-title">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium text-primary">{messages.tcoInputsEyebrow}</p>
              <h2
                id="tco-assumptions-title"
                className="mt-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
              >
                {messages.tcoInputsTitle}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {messages.tcoInputsBody}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={reset}
              className="self-start sm:self-auto"
            >
              <RotateCcw aria-hidden />
              {messages.tcoReset}
            </Button>
          </div>

          <div className="mt-8 grid gap-5 rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)] sm:grid-cols-3 lg:p-6">
            <NumberField
              id="tco-fleet-size"
              label={messages.tcoFleetSize}
              value={assumptions.fleetSize}
              step={100}
              onChange={(fleetSize) => setAssumptions((current) => ({ ...current, fleetSize }))}
            />
            <NumberField
              id="tco-years"
              label={messages.tcoOwnershipPeriod}
              value={assumptions.years}
              step={0.5}
              onChange={(years) => setAssumptions((current) => ({ ...current, years }))}
            />
            <label
              className="row-span-2 grid grid-rows-subgrid gap-2 text-sm font-medium text-foreground"
              htmlFor="tco-currency"
            >
              <span className="self-end">{messages.tcoCurrency}</span>
              <select
                id="tco-currency"
                value={currency}
                onChange={(event) => setCurrency(event.target.value as Currency)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {CURRENCIES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {([0, 1] as const).map((index) => (
              <article
                key={index}
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-soft)]"
              >
                <div
                  className={
                    index === 0
                      ? "border-b border-primary/20 bg-primary/5 px-5 py-4 lg:px-6"
                      : "border-b border-emerald-500/20 bg-emerald-500/5 px-5 py-4 lg:px-6"
                  }
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {messages.tcoScenarioLabel}
                  </p>
                  <h3 className="mt-1 text-xl font-semibold text-foreground">
                    {scenarioLabels[index]}
                  </h3>
                </div>
                <div className="grid gap-4 p-5 sm:grid-cols-2 lg:p-6">
                  {SCENARIO_FIELDS.map((field) => (
                    <NumberField
                      key={field.key}
                      id={`tco-${index}-${field.key}`}
                      label={messages[field.labelKey]}
                      value={scenarios[index][field.key]}
                      step={field.step}
                      onChange={(value) => updateScenario(index, field.key, value)}
                    />
                  ))}
                </div>
              </article>
            ))}
          </div>

          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            {messages.tcoDmsIncludedNote}
          </p>
        </RevealSection>

        <RevealSection aria-labelledby="tco-results-title" className="mt-14 lg:mt-20">
          <div className="text-center">
            <p className="text-sm font-medium text-primary">{messages.tcoResultsEyebrow}</p>
            <h2
              id="tco-results-title"
              className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl"
            >
              {messages.tcoResultsTitle}
            </h2>
          </div>

          <div
            aria-live="polite"
            className={
              isEqual
                ? "mx-auto mt-7 flex max-w-3xl items-start gap-3 rounded-2xl border border-border bg-muted/30 p-5"
                : "mx-auto mt-7 flex max-w-3xl items-start gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-5"
            }
          >
            {isEqual ? (
              <CircleEqual aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            ) : (
              <TrendingDown aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            )}
            <div>
              <p className="font-semibold text-foreground">
                {isEqual
                  ? messages.tcoEqualResult
                  : interpolate(messages.tcoLowerResult, {
                      scenario: scenarioLabels[lowerIndex],
                      amount: money.format(difference),
                      percent: new Intl.NumberFormat(locale, {
                        maximumFractionDigits: 1,
                      }).format(differencePercent),
                    })}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {messages.tcoComparisonNote}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {([0, 1] as const).map((index) => (
              <article
                key={index}
                className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-soft)] lg:p-7"
              >
                <p className="text-sm font-semibold text-muted-foreground">
                  {scenarioLabels[index]}
                </p>
                <p className="mt-2 text-4xl font-semibold tracking-tight text-foreground">
                  {money.format(results[index].total)}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{messages.tcoTotalLabel}</p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <Metric
                    label={messages.tcoPerDevice}
                    value={preciseMoney.format(results[index].perDevice)}
                  />
                  <Metric
                    label={messages.tcoPerDeviceMonth}
                    value={preciseMoney.format(results[index].perDevicePerMonth)}
                  />
                </div>

                <div className="mt-7 space-y-4">
                  {BREAKDOWN_FIELDS.map((field) => {
                    const value = results[index].breakdown[field.key];
                    const percentage =
                      results[index].total > 0 ? (value / results[index].total) * 100 : 0;
                    return (
                      <div key={field.key}>
                        <div className="flex items-baseline justify-between gap-4 text-sm">
                          <span className="text-muted-foreground">{messages[field.labelKey]}</span>
                          <span className="font-medium tabular-nums text-foreground">
                            {money.format(value)}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className={
                              index === 0
                                ? "h-full rounded-full bg-primary"
                                : "h-full rounded-full bg-emerald-500"
                            }
                            style={{ width: `${Math.min(100, percentage)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>
        </RevealSection>

        <RevealSection className="mt-16 border-t border-border pt-14 lg:mt-24 lg:pt-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-medium text-primary">{messages.tcoMethodEyebrow}</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {messages.tcoMethodTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              {messages.tcoMethodBody}
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              [messages.tcoMethod1Title, messages.tcoMethod1Body],
              [messages.tcoMethod2Title, messages.tcoMethod2Body],
              [messages.tcoMethod3Title, messages.tcoMethod3Body],
            ].map(([title, body], index) => (
              <article key={title} className="rounded-2xl border border-border bg-muted/20 p-5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {index + 1}
                </span>
                <h3 className="mt-4 font-semibold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </RevealSection>

        <RevealSection className="mt-14 overflow-hidden rounded-3xl bg-foreground px-6 py-10 text-background sm:px-10 lg:mt-20 lg:flex lg:items-center lg:justify-between lg:gap-10 lg:px-12">
          <div className="max-w-2xl">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background/10">
              <Coins aria-hidden className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-semibold tracking-tight sm:text-3xl">
              {messages.tcoCtaTitle}
            </h2>
            <p className="mt-3 leading-relaxed text-background/70">{messages.tcoCtaBody}</p>
          </div>
          <Button
            asChild
            size="lg"
            className="mt-6 bg-background text-foreground hover:bg-background/90 lg:mt-0"
          >
            <a href={u("/contacts?inquiry=sales#inquiry-form")}>
              {messages.tcoCtaButton}
              <ArrowRight aria-hidden />
            </a>
          </Button>
        </RevealSection>
      </main>
    </SiteLayout>
  );
}

function NumberField({
  id,
  label,
  value,
  step,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <label
      className="row-span-2 grid grid-rows-subgrid gap-2 text-sm font-medium text-foreground"
      htmlFor={id}
    >
      <span className="self-end">{label}</span>
      <Input
        id={id}
        type="number"
        min={0}
        step={step}
        inputMode="decimal"
        value={inputValue(value)}
        onChange={(event) => onChange(event.currentTarget.valueAsNumber)}
        className="h-10 bg-background tabular-nums"
      />
    </label>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <p className="text-lg font-semibold tabular-nums text-foreground">{value}</p>
      <p className="mt-1 text-xs leading-snug text-muted-foreground">{label}</p>
    </div>
  );
}
